import React, { useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useAuth } from '../../contexts/AuthContext';
import { isUserNotifyEvent } from '../../lib/userNotify';
import { dispatchUserNotify } from './notifyToast';

const API_URL = process.env.REACT_APP_API_URL || '';
const DEDUP_MAX = 400;

const NotifyStream: React.FC = () => {
  const { session } = useAuth();
  const seenIds = useRef<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!API_URL || !session?.access_token) {
      return;
    }

    const seen = seenIds.current;
    const remember = (id: string) => {
      if (seen.includes(id)) return false;
      seen.push(id);
      if (seen.length > DEDUP_MAX) {
        seen.splice(0, seen.length - DEDUP_MAX);
      }
      return true;
    };

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    void fetchEventSource(`${API_URL}/api/notify/stream`, {
      signal: ctrl.signal,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      async onopen(response) {
        if (response.ok) return;
        if (response.status === 503) {
          throw new Error('notifications_unavailable');
        }
        throw new Error(`notify_stream_${response.status}`);
      },
      onmessage(msg) {
        if (!msg.data) return;
        try {
          const parsed = JSON.parse(msg.data) as unknown;
          if (!isUserNotifyEvent(parsed)) return;
          if (!remember(parsed.id)) return;
          dispatchUserNotify(parsed);
        } catch {
          // ignore parse errors
        }
      },
      onerror(err) {
        if (err instanceof Error && err.message === 'notifications_unavailable') {
          throw err;
        }
      },
    }).catch((err) => {
      if (ctrl.signal.aborted) return;
      if (err instanceof Error && err.message === 'notifications_unavailable') {
        return;
      }
      console.warn('[NotifyStream] connection ended', err);
    });

    return () => {
      ctrl.abort();
      abortRef.current = null;
    };
  }, [session?.access_token]);

  return null;
};

export default NotifyStream;
